import { useState, useEffect } from "react";
import { Euro, TrendingUp, ShoppingCart, Users, BarChart2, RefreshCw } from "lucide-react";
import { API } from "./adminConstants";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const PRODUCT_LABELS = {
  persoonlijk_schema: "Pers. Schema",
  cursus: "Cursus",
  standaard_schema: "Stand. Schema",
  custom_schema: "Custom Schema",
};

const PRODUCT_COLORS = {
  persoonlijk_schema: "#C9A84C",
  cursus: "#60a5fa",
  standaard_schema: "#34d399",
  custom_schema: "#a78bfa",
};

function StatCard({ icon, label, value, accent = "text-[#C9A84C]", sub }) {
  return (
    <div className="bg-[#111] border border-white/8 rounded-2xl p-5">
      <div className={`${accent} mb-3`}>{icon}</div>
      <p className="text-white text-3xl font-bold leading-none">{value}</p>
      {sub && <p className="text-zinc-500 text-xs mt-1">{sub}</p>}
      <p className="text-zinc-600 text-xs mt-1 uppercase tracking-widest font-bold">{label}</p>
    </div>
  );
}

export default function AnalyticsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/analytics`, { headers: authHeaders });
      const d = await res.json();
      setData(d);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { load(); }, [token]);

  const fmt = (v) => `€${(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-zinc-600">
      <RefreshCw size={18} className="animate-spin mr-2" /> Laden…
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Overzicht</p>
          <h2 className="text-white font-bold text-xl">Analytics</h2>
        </div>
        <button onClick={load} className="p-2.5 text-zinc-600 hover:text-[#C9A84C] transition-colors bg-[#111] border border-white/8 rounded-lg">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={<Euro size={20} />} label="Totale omzet" value={fmt(data?.total_revenue)} accent="text-[#C9A84C]" sub="alle bestellingen" />
        <StatCard icon={<ShoppingCart size={20} />} label="Bestellingen" value={data?.total_orders ?? 0} accent="text-blue-400" sub="totaal ontvangen" />
        <StatCard icon={<Users size={20} />} label="Cursisten" value={data?.total_users ?? 0} accent="text-emerald-400" sub="actieve accounts" />
        <StatCard icon={<TrendingUp size={20} />} label="Gem. per order"
          value={data?.total_orders ? fmt(data.total_revenue / data.total_orders) : "–"}
          accent="text-purple-400" sub="gemiddeld bedrag" />
      </div>

      {/* Omzet per product */}
      {data?.revenue_by_product?.length > 0 && (
        <div className="bg-[#111] border border-white/8 rounded-2xl p-5">
          <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-4">Omzet per product</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {data.revenue_by_product.map(p => (
              <div key={p.product} className="bg-[#0a0a0a] border border-white/5 rounded-xl p-4">
                <div className="w-2 h-2 rounded-full mb-2" style={{ background: PRODUCT_COLORS[p.product] || "#888" }} />
                <p className="text-zinc-500 text-xs font-semibold uppercase tracking-wide mb-1 truncate">
                  {PRODUCT_LABELS[p.product] || p.product}
                </p>
                <p className="text-white font-bold text-lg">{p.count}×</p>
                <p className="font-semibold text-sm mt-0.5" style={{ color: PRODUCT_COLORS[p.product] || "#888" }}>
                  {fmt(p.total)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Wekelijkse grafiek */}
      {data?.weekly?.length > 0 && (
        <div className="bg-[#111] border border-white/8 rounded-2xl p-5">
          <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2">
            <BarChart2 size={14} /> Omzet per week (laatste {data.weekly.length} weken)
          </p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.weekly} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
              <XAxis dataKey="label" tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false}
                tickFormatter={v => `€${v}`} />
              <Tooltip
                contentStyle={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px" }}
                labelStyle={{ color: "#fff", fontWeight: "bold", fontSize: 12 }}
                itemStyle={{ color: "#C9A84C", fontSize: 12 }}
                formatter={(v) => [fmt(v), "Omzet"]}
              />
              <Bar dataKey="revenue" fill="#C9A84C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/5">
            {[
              { label: "Bestellingen", val: data.weekly.reduce((s, w) => s + w.orders, 0) },
              { label: "Omzet (periode)", val: fmt(data.weekly.reduce((s, w) => s + w.revenue, 0)) },
              { label: "Actieve weken", val: data.weekly.filter(w => w.orders > 0).length },
            ].map(({ label, val }) => (
              <div key={label} className="text-center">
                <p className="text-white font-bold text-lg">{val}</p>
                <p className="text-zinc-600 text-xs mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(!data?.total_orders || data.total_orders === 0) && (
        <div className="text-center py-16 border border-dashed border-white/8 rounded-2xl">
          <BarChart2 size={32} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">Nog geen bestellingen — analytics komen hier te staan</p>
        </div>
      )}
    </div>
  );
}
